/**
 * OsHandoffView (PRD-004 scoping pass §6 TECH-OS-01/02, PR-OS-03; final design
 * pass HandoffView).
 *
 * The owner-facing handoff surface, shown below the status line once the
 * engagement is at or past handoff_ready and a handoff_deliverable_id is set.
 * It matches the Claude Design HandoffView (design-final/Studio OS.html), wired
 * to the REAL deliverable + files (loaded via lib/os.ts, Batch 3):
 *
 *   1. A green-accent "Your system / ready" overline + a hairline rule above it.
 *   2. The README, rendered from the REAL `deliverable.content.readme_md` via the
 *      shared Markdown component. A serif title is derived from the README's own
 *      first H1 when present (else a calm default), with the
 *      "README / written around your own words from session one" subline.
 *   3. The navy Walkthrough card wrapping the REAL always-on walkthrough video
 *      (`deliverable.video_url` / `video_provider` via `toEmbedUrl`). TECH-OS-02:
 *      always-on for the OS owner, no tier check, no Pro gate.
 *   4. The "Say it back" MentalModel block from `deliverable.content.mental_model`.
 *   5. The Downloads block from the REAL `os_handoff_files` rows: a "Download
 *      everything" button + per-file rows with a derived kind chip; each download
 *      mints a fresh signed URL on click (createHandoffFileSignedUrl).
 *   6. The conversion door, rendered only at the `open` stage, linking to the
 *      marketing site services page.
 *
 * Warm-brand tokens (owner surface), matching OsStatusComponent. Read-only:
 * the owner never mutates anything here.
 *
 * DOWNLOAD UX (honest scope note): "Download everything" is V1-sequential. It
 * mints and opens each file's signed URL in turn (a short stagger so the
 * browser does not collapse them into one navigation). A true server-side ZIP
 * is a documented follow-up, NOT this batch (no service-role on Railway,
 * ARCH-002, so a zip would need a new RPC/endpoint - out of scope here).
 *
 * KIND CHIP (no `kind` column on os_handoff_files): derived client-side from the
 * file's mime_type / filename extension into a short uppercase chip (e.g. md ->
 * DOC, ts/json -> SKILL), falling back to the extension or "FILE".
 */

import { useState } from 'react';
import { toEmbedUrl } from '@/components/portal/renderers/video';
import { Markdown } from '@/components/portal/renderers/Markdown';
import {
  createHandoffFileSignedUrl,
  type OwnHandoffDeliverable,
  type OwnHandoffFile,
} from '@/lib/os';

/** The marketing-site services page the conversion door links to. */
const STUDIO_SERVICES_URL = 'https://yesplease.studio/services';

/**
 * Split the README markdown into an optional leading H1 title and the remaining
 * body. When the README opens with an ATX H1 (`# Title`) we lift it into the
 * serif heading and render the rest as the body, so the title is not shown
 * twice. When there is no leading H1 we keep the whole README as the body and
 * use a calm default title.
 */
function splitReadmeTitle(readmeMd: string): { title: string; body: string } {
  const lines = readmeMd.replace(/\r\n/g, '\n').split('\n');
  let idx = 0;
  while (idx < lines.length && (lines[idx] ?? '').trim() === '') idx += 1;
  const first = lines[idx];
  const h1 = first !== undefined ? /^#\s+(.+)$/.exec(first.trim()) : null;
  if (h1 && h1[1]) {
    const body = lines.slice(idx + 1).join('\n').trim();
    return { title: h1[1].trim(), body };
  }
  return { title: 'Your system.', body: readmeMd };
}

/**
 * Derive a short uppercase kind chip for a handoff file. os_handoff_files has no
 * `kind` column, so we infer it from the mime type or filename extension. The
 * mapping is intentionally small and on-theme (the artifacts are skills, docs,
 * templates, and shortcuts); anything unrecognized falls back to the extension
 * (uppercased) or "FILE".
 */
function kindChip(file: OwnHandoffFile): string {
  const ext = (file.filename.split('.').pop() ?? '').toLowerCase();
  const mime = (file.mime_type ?? '').toLowerCase();
  if (ext === 'md' || ext === 'markdown' || mime.includes('markdown')) {
    return 'DOC';
  }
  if (ext === 'ts' || ext === 'js' || ext === 'json') return 'SKILL';
  if (ext === 'csv' || ext === 'tsv') return 'TEMPLATE';
  if (ext === 'shortcut' || ext === 'wflow') return 'SHORTCUT';
  if (ext === 'pdf') return 'PDF';
  if (ext === 'zip') return 'ZIP';
  if (ext.length > 0 && ext.length <= 5) return ext.toUpperCase();
  return 'FILE';
}

/**
 * Open one file in a new tab by minting its signed URL. Returns on completion;
 * throws on a storage/RLS failure so the caller can surface it.
 */
async function openSignedFile(storagePath: string): Promise<void> {
  const url = await createHandoffFileSignedUrl(storagePath);
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function OsHandoffView({
  deliverable,
  files,
  showConversion,
}: {
  deliverable: OwnHandoffDeliverable;
  files: OwnHandoffFile[];
  showConversion: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busyPath, setBusyPath] = useState<string | null>(null);
  const [downloadingAll, setDownloadingAll] = useState(false);

  // TECH-OS-02: always-on. No tier check; render whenever a URL is present.
  const embedUrl = toEmbedUrl(deliverable.video_url, deliverable.video_provider);
  const { title, body } = splitReadmeTitle(deliverable.content.readme_md);

  async function onDownloadOne(file: OwnHandoffFile): Promise<void> {
    setError(null);
    setBusyPath(file.storage_path);
    try {
      await openSignedFile(file.storage_path);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Could not download ${file.filename}.`,
      );
    } finally {
      setBusyPath(null);
    }
  }

  async function onDownloadAll(): Promise<void> {
    setError(null);
    setDownloadingAll(true);
    try {
      // V1 sequential: mint + open each in turn with a small stagger so the
      // browser treats them as distinct downloads rather than one navigation.
      // A server-side zip is a documented follow-up (see file header).
      for (const file of files) {
        await openSignedFile(file.storage_path);
        await new Promise((resolve) => window.setTimeout(resolve, 400));
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not download every file. Try them one at a time below.',
      );
    } finally {
      setDownloadingAll(false);
    }
  }

  return (
    <section aria-label="Your handoff">
      <div className="my-[clamp(36px,5vw,52px)] h-px bg-[color:var(--color-rule)]" />
      <p className="mb-4 font-semibold text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-green-accent)]">
        Your system &middot; ready
      </p>

      <div className="flex flex-col gap-[clamp(36px,5vw,52px)]">
        {/* README, real markdown in the client's voice */}
        <div className="max-w-[600px]">
          <h2 className="mb-1 font-display text-[clamp(28px,3.4vw,40px)] leading-[1.08] tracking-[-0.01em] text-[color:var(--color-black)]">
            {title}
          </h2>
          <p className="mb-[22px] text-[15px] text-[color:var(--color-text-mute)]">
            README &middot; written around your own words from session one
          </p>
          <Markdown source={body} />
        </div>

        {/* Walkthrough card wrapping the real always-on video (TECH-OS-02) */}
        {embedUrl ? (
          <div className="relative max-w-[600px] overflow-hidden rounded-xl bg-[color:var(--color-navy-deepest)]">
            <div className="px-6 pb-0 pt-[26px]">
              <p className="mb-[7px] font-semibold text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-rose)]">
                A personal walkthrough
              </p>
              <p className="mb-1 text-[17px] font-medium text-[color:var(--color-linen)]">
                How your system works, start to finish.
              </p>
              <p className="mb-[18px] text-[13px] text-[color:var(--color-linen)]/80">
                I recorded this for you. Worth watching before we install it
                together.
              </p>
            </div>
            <div className="aspect-video w-full bg-[color:var(--color-navy-deepest)]">
              <iframe
                src={embedUrl}
                title="Walkthrough video"
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        ) : null}

        {/* "Say it back" mental model, from the real deliverable content */}
        <div className="max-w-[600px]">
          <p className="mb-3.5 font-semibold text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-rose-dark)]">
            Say it back
          </p>
          <p className="font-display text-[clamp(24px,3vw,34px)] leading-[1.25] tracking-[-0.01em] text-[color:var(--color-black)] text-pretty">
            {deliverable.content.mental_model}
          </p>
          <p className="mt-3.5 text-sm text-[color:var(--color-text-mute)]">
            If you can say that line back, you own it. That&apos;s the whole
            test.
          </p>
        </div>

        {/* Downloads, from the real os_handoff_files rows */}
        {files.length > 0 ? (
          <div className="max-w-[600px]">
            <p className="mb-4 font-semibold text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-text-mute)]">
              Yours to keep
            </p>
            <button
              type="button"
              onClick={() => void onDownloadAll()}
              disabled={downloadingAll}
              className="mb-2.5 flex w-full items-center gap-3.5 rounded-[10px] bg-[color:var(--color-navy)] px-5 py-4 text-left transition-colors hover:bg-[color:var(--color-navy-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-navy)] disabled:opacity-60"
            >
              <span
                className="text-lg text-[color:var(--color-linen)]"
                aria-hidden="true"
              >
                &darr;
              </span>
              <span className="flex-1">
                <span className="block text-[15px] font-semibold text-[color:var(--color-linen)]">
                  {downloadingAll ? 'Opening files...' : 'Download everything'}
                </span>
                <span className="mt-px block text-[13px] text-[color:var(--color-linen)]/80">
                  {title} &middot; full system, file by file
                </span>
              </span>
              <span className="text-xs font-medium text-[color:var(--color-rose)]">
                {files.length} files
              </span>
            </button>

            <ul className="overflow-hidden rounded-[10px] border border-[color:var(--color-rule)]">
              {files.map((file, i) => (
                <li
                  key={file.id}
                  className={[
                    'flex items-center gap-3.5 bg-[color:var(--color-white)] px-[18px] py-[13px]',
                    i < files.length - 1
                      ? 'border-b border-[color:var(--color-rule)]'
                      : '',
                  ].join(' ')}
                >
                  <span className="shrink-0 rounded-[3px] border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] px-[7px] py-0.5 text-[10px] font-semibold tracking-[0.06em] text-[color:var(--color-gray-500)]">
                    {kindChip(file)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-[color:var(--color-black)]">
                      {file.label ?? file.filename}
                    </span>
                    <span className="mt-px block truncate text-xs text-[color:var(--color-gray-500)]">
                      {file.filename}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => void onDownloadOne(file)}
                    disabled={busyPath === file.storage_path}
                    className="shrink-0 text-sm font-medium text-[color:var(--color-navy)] underline underline-offset-4 hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-navy)] disabled:opacity-60"
                  >
                    {busyPath === file.storage_path ? 'Opening...' : 'Download'}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {error ? (
        <p
          role="alert"
          className="mt-6 rounded-md border border-[color:var(--color-terracotta)] px-3 py-2 text-sm text-[color:var(--color-terracotta)]"
        >
          {error}
        </p>
      ) : null}

      {showConversion ? (
        <div className="mt-[clamp(40px,6vw,64px)] max-w-[600px] rounded-[14px] bg-[color:var(--color-linen)] p-[clamp(26px,4vw,40px)]">
          <p className="mb-3.5 font-semibold text-[11px] uppercase tracking-[0.14em] text-[color:var(--color-rose-dark)]">
            The door&apos;s open
          </p>
          <h3 className="mb-3 font-display text-[clamp(24px,3vw,32px)] leading-[1.1] tracking-[-0.01em] text-[color:var(--color-black)]">
            Whenever you&apos;re ready for the next one.
          </h3>
          <p className="mb-5 max-w-[480px] text-base leading-relaxed text-[color:var(--color-gray-500)] text-pretty">
            A Studio subscription is this same working relationship, made
            continuous. One thing at a time, no scheduling, paused or stopped
            whenever you like. Have a look when the timing feels right.
          </p>
          <a
            href={STUDIO_SERVICES_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 border-b-[1.5px] border-[color:var(--color-navy)] pb-0.5 text-[15px] font-medium text-[color:var(--color-navy)] hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-navy)]"
          >
            See how Studio works
            <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      ) : null}
    </section>
  );
}
